import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { TransactionKey } from 'api/Transaction/Types/Transaction'
import { Link } from 'components/Link'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'

import './Create/transaction.scss'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

export const transactionIBTransactionsColumns: GridColumn[] = [
  {
    title: 'Type',
    field: TransactionKey.type,
    cell: (props: GridCellProps): JSX.Element => {
      if (isEmpty(props.dataItem.type)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.type.name}
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'IB number',
    field: TransactionKey.ibNumber,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.introducingBroker?.name) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsIntroducingBrokers.detailsUrl as string, {
                id: props.dataItem.introducingBroker.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.introducingBroker.name}
          </Link>
        </td>
      )
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Debit Amount',
    field: TransactionKey.amount,
    cell: (props: GridCellProps): JSX.Element => {
      if (isEmpty(props.dataItem.walletDebitAmount)) {
        return <td className={generateClassNameForReverseType(props.dataItem, 'number')} />
      }
      const amount = props.dataItem?.walletDebitAmount?.toFixed(2)
      return <td className={generateClassNameForReverseType(props.dataItem, 'number')}>{amount}</td>
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'Credit Amount',
    field: TransactionKey.amount,
    cell: (props: GridCellProps): JSX.Element => {
      if (isEmpty(props.dataItem.walletCreditAmount)) {
        return <td className={generateClassNameForReverseType(props.dataItem, 'number')} />
      }
      const amount = props.dataItem?.walletCreditAmount?.toFixed(2)
      return <td className={generateClassNameForReverseType(props.dataItem, 'number')}>{amount}</td>
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'Currency',
    field: TransactionKey.currency,
    cell: (props: GridCellProps): JSX.Element => {
      if (isEmpty(props.dataItem.currency)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      const currencyId = props.dataItem.currency.id
      return <td className={generateClassNameForReverseType(props.dataItem)}>{currencyId}</td>
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: '# of Clients',
    field: TransactionKey.clientCount,
    cell: (gridCellProps: GridCellProps) => (
      <td className={generateClassNameForReverseType(gridCellProps.dataItem, 'number')}>
        {gridCellProps.dataItem.clientCount}
      </td>
    ),
    show: true,
  },
  {
    title: '# of Wallets',
    field: TransactionKey.walletCount,
    cell: (gridCellProps: GridCellProps) => (
      <td className={generateClassNameForReverseType(gridCellProps.dataItem, 'number')}>
        {gridCellProps.dataItem.walletCount}
      </td>
    ),
    show: true,
  },
  {
    title: 'Status',
    field: TransactionKey.state,
    show: true,
    cell: (gridCellProps: GridCellProps) => (
      <td className={generateClassNameForReverseType(gridCellProps.dataItem)}>
        {gridCellProps.dataItem.state.name}
      </td>
    ),
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Entity',
    field: TransactionKey.tickmillCompany,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.tickmillCompany) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      const company = props.dataItem.tickmillCompany
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          <CompanyTag id={company.id}>{company.name}</CompanyTag>
        </td>
      )
    },
    resizable: true,
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: TransactionKey.createdDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem)}>
        {formatLocalDateUTC(props.dataItem.createdDate, true)}
      </td>
    ),
    show: true,
    resizable: true,
    filter: 'text',
    width: 120,
    minResizableWidth: 120,
  },
  {
    title: 'Date Updated',
    field: TransactionKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem)}>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    show: true,
    resizable: true,
    filter: 'text',
    width: 120,
    minResizableWidth: 120,
  },
]
